import { AlertTriangle, Check } from "lucide-react";

import { CARD_WALL_RADIUS } from "@/shared/ui/cardWall";
import { cn } from "@/shared/lib/utils";

import type { DomainCard } from "../../api/types";
import { chainPosition, domainChain } from "../../model/domainStatus";

/**
 * 屏 5 右栏「域名状态」:未绑定 → 待配置 → 验证中 → 已验证 → 解析检测中 → 签发证书 → 已上线。
 *
 * ★ 当前节从 `overallStatus` 派生(`chainPosition`),零新状态;`error` 不是一节,它把出问题的那一节标红,
 *   下面带 api 的 `lastCheckNote`。
 * ★ 不可点(与 StepIndicator 同一条纪律):整条里不出现 button / a。
 * ★ 设计稿脚注写「…标红并通知管理员」,api 这期没有通知口,只写「标红」,不许诺做不到的事。
 * ★ 模拟发布(`simulated`,附录 A.38):「签发证书」一节叫「上线」,脚注不提证书 —— stub 网关不签证书。
 */
export function DomainStatusChain({
  card,
  simulated = false,
  hostNote = null,
}: {
  card: Pick<
    DomainCard,
    "overallStatus" | "verificationStatus" | "dnsStatus" | "sslStatus" | "lastCheckNote"
  > | null;
  simulated?: boolean;
  /** host 模式宿主签发状态的人话(A.54),挂在「签发证书」那一节下面;只在停在 issuing(或那一节出错)时出现。 */
  hostNote?: string | null;
}) {
  const { index, error } = chainPosition(card);
  const chain = domainChain(simulated);
  return (
    <section
      aria-label="域名状态"
      className={cn("border border-border bg-card p-4", CARD_WALL_RADIUS)}
    >
      <h2 className="mb-3 text-sm font-semibold text-foreground">域名状态</h2>
      <ol
        aria-label="域名状态链"
        data-domain-chain={card?.overallStatus ?? "unbound"}
        className="space-y-1.5"
      >
        {chain.map((node, i) => {
          const done = i < index || (i === index && node.status === "live" && !error);
          const active = i === index;
          const failed = active && error;
          return (
            <li
              key={node.status}
              aria-current={active ? "step" : undefined}
              data-state={failed ? "error" : active ? "current" : done ? "done" : "todo"}
              className={cn(
                "flex items-center gap-2 rounded-md px-2 py-1 text-xs",
                active && !failed && "bg-primary/10 font-semibold text-primary",
                // 红字压在淡红底上深色皮肤对比不够(QueryErrorBanner 同款教训):红靠描边 + 图标,字用前景色。
                failed &&
                  "border border-destructive/50 bg-destructive/10 font-semibold text-foreground",
                !active && done && "text-muted-foreground",
                !active && !done && "text-muted-foreground/70"
              )}
            >
              <span
                aria-hidden
                className={cn(
                  "grid h-4 w-4 shrink-0 place-items-center rounded-full text-2xs",
                  failed && "bg-destructive text-destructive-foreground",
                  active && !failed && "bg-primary text-primary-foreground",
                  !active && done && "bg-muted text-muted-foreground",
                  !active && !done && "border border-border/70"
                )}
              >
                {failed ? (
                  <AlertTriangle className="h-2.5 w-2.5" />
                ) : done ? (
                  <Check className="h-2.5 w-2.5" />
                ) : null}
              </span>
              <span className="min-w-0">
                {node.label}
                {failed && <span className="sr-only">(异常)</span>}
                {active && node.status === "issuing" && hostNote && (
                  <span
                    data-testid="host-cert-note"
                    className="block text-2xs font-normal text-muted-foreground"
                  >
                    {hostNote}
                  </span>
                )}
              </span>
            </li>
          );
        })}
      </ol>
      {error && card?.lastCheckNote && (
        <p
          role="note"
          className="mt-2 rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1 text-xs text-foreground"
        >
          {card.lastCheckNote}
        </p>
      )}
      <p className="mt-3 text-2xs text-muted-foreground">
        {simulated
          ? "检测发现异常(解析被改)会在这里标红。"
          : "检测发现异常(证书失败 / 解析被改)会在这里标红。"}
      </p>
    </section>
  );
}
